import React from 'react';
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useState } from "react";
import './styles.css'

// Add these helper functions at the top of your file, outside the component
const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const formatZipCode = (value) => {
  if (!value) return value;
  const zipCode = value.replace(/[^\d]/g, '');
  if (zipCode.length <= 5) return zipCode;
  return `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`;
};

const US_STATES = [
  { value: '', label: 'Select a state' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District Of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const API_URL = 'https://shopify-affiliate-registration-form.vercel.app/api/submit-application';

const RegistrationForm = () => {
  const { register, handleSubmit, watch, formState: { errors }, setValue, trigger } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });
  const [sameAsShipping, setSameAsShipping] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ loading: false, error: null });
  const [shopifyConfig, setShopifyConfig] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  useEffect(() => {
    // Get Shopify configuration if embedded
    if (window.Shopify) {
      const shop = window.Shopify.shop;
      setShopifyConfig({ shop });
    }
  }, []);

  const watchBusinessDocuments = watch("businessDocuments");
  
  useEffect(() => {
    if (watchBusinessDocuments && watchBusinessDocuments[0]) {
      setSelectedFile(watchBusinessDocuments[0]);
    } else {
      setSelectedFile(null);
    }
  }, [watchBusinessDocuments]);

  useEffect(() => {
    if (sameAsShipping) {
      setValue('billingAddress1', watch('shippingAddress1'));
      setValue('billingAddress2', watch('shippingAddress2'));
      setValue('billingCity', watch('shippingCity'));
      setValue('billingState', watch('shippingState'));
      setValue('billingZip', watch('shippingZip'));
    }
  }, [sameAsShipping, watch('shippingAddress1'), watch('shippingAddress2'), 
      watch('shippingCity'), watch('shippingState'), watch('shippingZip')]);

  const onSubmit = async (data) => {
    setSubmitStatus({ loading: true, error: null });
    try {
      // Validate email to prevent "Missing to field" error from the API
      if (!data.email) {
        throw new Error('Email address is required');
      }
      
      const formData = new FormData();
      
      // Create a JSON object with just the application data
      const jsonData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        shippingAddress: {
          address1: data.shippingAddress1,
          address2: data.shippingAddress2,
          city: data.shippingCity,
          state: data.shippingState,
          zip: data.shippingZip
        },
        billingAddress: {
          address1: data.sameAsShipping ? data.shippingAddress1 : data.billingAddress1,
          address2: data.sameAsShipping ? data.shippingAddress2 : data.billingAddress2,
          city: data.sameAsShipping ? data.shippingCity : data.billingCity,
          state: data.sameAsShipping ? data.shippingState : data.billingState,
          zip: data.sameAsShipping ? data.shippingZip : data.billingZip
        },
        businessInfo: {
          name: data.businessName,
          type: data.businessType,
          email: data.businessEmail,
          phone: data.businessPhone,
          website: data.businessWebsite,
          address1: data.businessAddress1,
          address2: data.businessAddress2,
          city: data.businessCity,
          state: data.businessState,
          zip: data.businessZip,
          instagram: data.instagramProfile,
          linkedin: data.linkedinProfile
        },
        referralSource: data.referralSource,
        emailUpdates: data.emailUpdates,
        submissionDate: new Date().toISOString()
      };

      // Add file if it exists
      if (data.businessDocuments?.[0]) {
        formData.append('businessDocuments', data.businessDocuments[0]);
      }
      
      // Add the JSON data as a string
      formData.append('data', JSON.stringify(jsonData));

      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit form');
      }

      setSubmitStatus({ loading: false, error: null });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus({ 
        loading: false, 
        error: error.message || 'Failed to submit form. Please try again.' 
      });
    }
  };

  const onError = (errors) => {
    console.log('Form errors:', errors);
  };

  const businessTypes = [
    "Designer/Architect",
    "Contractor",
    "Fabricator",
    "Installer",
    "Design & Build Firm",
    "Showroom/Retailer",
    "Other"
  ];

  const hearAboutUs = [
    "Google/Search Engine",
    "Social Media",
    "Email",
    "Recommendation",
    "Other"
  ];

  // Update the handlePhoneChange function:
  const handlePhoneChange = (e) => {
    const field = e.target.name;
    let value = e.target.value;
    
    // Only allow numeric input
    value = value.replace(/\D/g, '');
    
    // If the number starts with '1', remove it
    if (value.startsWith('1')) {
      value = value.slice(1);
    }
    
    const formatted = formatPhoneNumber(value);
    setValue(field, formatted, { shouldValidate: false });
    e.target.value = formatted;
  };

  const handleShippingZipChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    const formatted = formatZipCode(value);
    setValue('shippingZip', formatted, { shouldValidate: false });
    e.target.value = formatted;
  };

  const handleBillingZipChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    const formatted = formatZipCode(value);
    setValue('billingZip', formatted, { shouldValidate: false });
    e.target.value = formatted;
  };

  const handleBusinessPhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    if (value.startsWith('1')) {
      value = value.slice(1);
    }
    const formatted = formatPhoneNumber(value);
    setValue('businessPhone', formatted, { shouldValidate: false });
    e.target.value = formatted;
  };

  // First, add a new handler for business ZIP
  const handleBusinessZipChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    const formatted = formatZipCode(value);
    setValue('businessZip', formatted, { shouldValidate: false });
    e.target.value = formatted;
  };

  // Update the autofill handler
  const handleAutoFill = (e) => {
    const field = e.target.name;
    const value = e.target.value;
    
    if (field === 'phone') {
      handlePhoneChange(e);
    } else if (field === 'shippingZip') {
      handleShippingZipChange(e);
    } else if (field === 'billingZip') {
      handleBillingZipChange(e);
    } else {
      setValue(field, value, { shouldValidate: false });
    }
  };

  // Update website validation
  const validateWebsite = (url) => {
    if (!url) return true; // Optional field
    
    // Remove any leading/trailing whitespace
    const trimmedUrl = url.trim();
    
    // Try different URL formats
    const urlFormats = [
      trimmedUrl,                    // Original input
      `https://${trimmedUrl}`,       // Add https://
      `http://${trimmedUrl}`         // Add http://
    ];

    for (const format of urlFormats) {
      try {
        const urlObject = new URL(format);
        // Check if the URL has a valid domain (at least one dot)
        if (urlObject.hostname.includes('.')) {
          return true;
        }
      } catch {
        continue;
      }
    }

    // If none of the above worked, check if it might be a valid domain without protocol
    const domainPattern = /^(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/;
    if (domainPattern.test(trimmedUrl)) {
      return true;
    }

    return false;
  };

  // Update the checkbox handler to fill in billing fields
  const handleSameAsShipping = (e) => {
    const checked = e.target.checked;
    setSameAsShipping(checked);
    
    if (checked) {
      // Copy shipping address values to billing address fields
      setValue('billingAddress1', watch('shippingAddress1'));
      setValue('billingAddress2', watch('shippingAddress2'));
      setValue('billingCity', watch('shippingCity'));
      setValue('billingState', watch('shippingState'));
      setValue('billingZip', watch('shippingZip'));
    } else {
      // Clear billing address fields
      setValue('billingAddress1', '');
      setValue('billingAddress2', '');
      setValue('billingCity', '');
      setValue('billingState', '');
      setValue('billingZip', '');
    }
  };

  // Add success message component
  if (isSubmitted) {
    return (
      <div className="form-mx-auto form-p-6 form-rounded-lg form-text-center" style={{ backgroundColor: '#EFEFEF' }}>
        <h2 className="form-text-2xl form-font-bold form-mb-4">Thank You!</h2>
        <p className="form-mb-4">Your application has been successfully submitted.</p>
        <p className="form-text-gray-600">
          You're nearly finished... Please check your email to complete the application process.
          If you have any questions, please contact us at{' '}
          <a href="mailto:trade@tilemart.com" className="form-underline">
            trade@tilemart.com
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="affiliate-form-wrapper form-w-full">
      <form 
        onSubmit={handleSubmit(onSubmit, onError)} 
        className="form-mx-auto form-rounded-lg responsive-form form-p-6 form-sm:p-12" 
        style={{ 
          backgroundColor: '#EFEFEF'
        }}
        id="responsive-form"
      >
        {/* Header with title and contact */}
        <div className="form-header form-grid form-grid-cols-1 form-sm:grid-cols-2 form-mb-4">
          <div className="form-mb-4 form-sm:mb-0">
            <h1 className="form-text-2xl form-sm:text-2xl form-font-bold form-normal-case">Sign up for Tile Mart Trade</h1>
          </div>
          <div>
            <p className="form-text-sm form-sm:text-base form-text-gray-500 form-text-left form-sm:text-right">
              Questions? Contact us at <a href="mailto:trade@tilemart.com" className="form-underline">trade@tilemart.com</a>
            </p>
          </div>
        </div>

        {/* Account Information */}
        <section className="form-mb-8">
          <h2 className="form-text-xl form-sm:text-2xl form-font-bold form-mb-4">Step 1: Account Information</h2>
          
          {/* Name fields - Two columns side by side on desktop, single column on mobile */}
          <div className="form-grid form-grid-cols-1 form-sm:grid-cols-2 form-gap-4 form-mb-4">
            <div>
              <label className="form-block form-mb-1 form-font-bold">First Name*</label>
              <input 
                {...register("firstName", { required: "First name is required" })}
                onAnimationStart={handleAutoFill}
                autoComplete="given-name"
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.firstName ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              />
              {errors.firstName && <span className="form-text-red-500 form-text-sm">{errors.firstName.message}</span>}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">Last Name*</label>
              <input 
                {...register("lastName", { required: "Last name is required" })}
                onAnimationStart={handleAutoFill}
                autoComplete="family-name"
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.lastName ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              />
              {errors.lastName && <span className="form-text-red-500 form-text-sm">{errors.lastName.message}</span>}
            </div>
          </div>

          {/* Email field - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Email*</label>
            <input 
              type="email"
              {...register("email", { 
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                },
                validate: (value) => {
                  return value && value.trim() !== "" || "Email cannot be empty";
                }
              })}
              autoComplete="email"
              className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                errors.email ? 'form-border-red-500' : 'form-border-gray-300'
              }`}
            />
            {errors.email && <span className="form-text-red-500 form-text-sm">{errors.email.message}</span>}
          </div>

          {/* Phone field - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Phone*</label>
            <input 
              type="tel"
              inputMode="numeric"
              {...register("phone", { 
                required: "Phone number is required",
                pattern: {
                  value: /^\d{3}-\d{3}-\d{4}$/,
                  message: "Please enter a valid phone number (XXX-XXX-XXXX)"
                }
              })}
              onChange={handlePhoneChange}
              autoComplete="tel"
              placeholder="XXX-XXX-XXXX"
              className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                errors.phone ? 'form-border-red-500' : 'form-border-gray-300'
              }`}
            />
            {errors.phone && <span className="form-text-red-500 form-text-sm">{errors.phone.message}</span>}
          </div>

          {/* Address Line 1 - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Address Line 1*</label>
            <input 
              {...register("shippingAddress1", { required: "Address is required" })}
              className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${errors.shippingAddress1 ? 'form-border-red-500' : 'form-border-gray-300'}`}
            />
            {errors.shippingAddress1 && <span className="form-text-red-500 form-text-sm">{errors.shippingAddress1.message}</span>}
          </div>

          {/* Address Line 2 - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Address Line 2</label>
            <input 
              {...register("shippingAddress2")}
              className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
            />
          </div>

          {/* City, State, ZIP row - Three columns */}
          <div className="form-grid form-grid-cols-1 form-sm:grid-cols-3 form-gap-4 form-mb-4">
            <div>
              <label className="form-block form-mb-1 form-font-bold">City*</label>
              <input 
                {...register("shippingCity", { required: "City is required" })}
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${errors.shippingCity ? 'form-border-red-500' : 'form-border-gray-300'}`}
              />
              {errors.shippingCity && <span className="form-text-red-500 form-text-sm">{errors.shippingCity.message}</span>}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">State*</label>
              <select
                {...register("shippingState", { 
                  required: "State is required",
                  validate: value => value !== '' || "Please select a state"
                })}
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.shippingState ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              >
                {US_STATES.map(state => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.shippingState && (
                <span className="form-text-red-500 form-text-sm">{errors.shippingState.message}</span>
              )}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">ZIP Code*</label>
              <input 
                {...register("shippingZip", { 
                  required: "ZIP Code is required",
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Please enter a valid ZIP code"
                  }
                })}
                onChange={handleShippingZipChange}
                autoComplete="postal-code"
                placeholder="XXXXX"
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.shippingZip ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              />
              {errors.shippingZip && <span className="form-text-red-500 form-text-sm">{errors.shippingZip.message}</span>}
            </div>
          </div>
        </section>

        {/* Separator between Step 1 and Step 2 */}
        <div style={{ 
          borderTop: '1px dotted #A7A7A7', 
          margin: '2rem 0' 
        }}></div>

        {/* Billing Address */}
        <section className="form-mb-8">
          <h2 className="form-text-xl form-sm:text-2xl form-font-bold form-mb-4">Step 2: Billing Address</h2>
          <div className="form-mb-4">
            <label className="form-flex form-items-center form-font-bold">
              <input
                type="checkbox"
                checked={sameAsShipping}
                onChange={handleSameAsShipping}
              />
              <span className="form-ml-2 form-text-sm form-sm:text-base">Same as shipping address</span>
            </label>
          </div>
          
          {/* Address Line 1 - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Address Line 1*</label>
            <input 
              {...register("billingAddress1", { required: "Address is required" })}
              className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${errors.billingAddress1 ? 'form-border-red-500' : 'form-border-gray-300'}`}
            />
            {errors.billingAddress1 && <span className="form-text-red-500 form-text-sm">{errors.billingAddress1.message}</span>}
          </div>

          {/* Address Line 2 - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Address Line 2</label>
            <input 
              {...register("billingAddress2")}
              className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
            />
          </div>

          {/* City, State, ZIP row - Three columns */}
          <div className="form-grid form-grid-cols-1 form-sm:grid-cols-3 form-gap-4 form-mb-4">
            <div>
              <label className="form-block form-mb-1 form-font-bold">City*</label>
              <input 
                {...register("billingCity", { required: "City is required" })}
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${errors.billingCity ? 'form-border-red-500' : 'form-border-gray-300'}`}
              />
              {errors.billingCity && <span className="form-text-red-500 form-text-sm">{errors.billingCity.message}</span>}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">State*</label>
              <select
                {...register("billingState", { 
                  required: "State is required",
                  validate: value => value !== '' || "Please select a state"
                })}
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.billingState ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              >
                {US_STATES.map(state => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.billingState && (
                <span className="form-text-red-500 form-text-sm">{errors.billingState.message}</span>
              )}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">ZIP Code*</label>
              <input 
                {...register("billingZip", { 
                  required: "ZIP Code is required",
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Please enter a valid ZIP code"
                  }
                })}
                onChange={handleBillingZipChange}
                placeholder="XXXXX"
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.billingZip ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              />
              {errors.billingZip && <span className="form-text-red-500 form-text-sm">{errors.billingZip.message}</span>}
            </div>
          </div>
        </section>

        {/* Separator between Step 2 and Step 3 */}
        <div style={{ 
          borderTop: '1px dotted #A7A7A7', 
          margin: '2rem 0' 
        }}></div>

        {/* Business Information */}
        <section className="form-mb-8">
          <h2 className="form-text-xl form-sm:text-2xl form-font-bold form-mb-4">Step 3: Business Information</h2>
          
          {/* Business Types - 3 columns on desktop, 2 on mobile */}
          <div className="form-mb-4">
            <label className="form-text-sm form-sm:text-base form-font-bold">Tell us about yourself* (Check all that apply)</label>
            <div className="form-grid form-grid-cols-2 form-sm:grid-cols-3 form-gap-4 form-mt-2">
              {/* Column 1 */}
              <div>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Designer/Architect"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Designer/Architect</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Contractor"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Contractor</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Fabricator"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Fabricator</span>
                </label>
              </div>
              
              {/* Column 2 */}
              <div>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Installer"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Installer</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Design & Build Firm"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Design & Build Firm</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Showroom/Retailer"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Showroom/Retailer</span>
                </label>
              </div>
              
              {/* Column 3 */}
              <div className="form-col-span-2 form-sm:col-span-1">
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="checkbox"
                    {...register("businessType", { 
                      required: "Please select at least one business type" 
                    })}
                    value="Other"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Other</span>
                </label>
                
                {/* "Please specify" input field in Column 3 */}
                {(watch("businessType") || []).includes("Other") && (
                  <input
                    {...register("businessTypeOther")}
                    placeholder="Please specify"
                    className="form-mt-1 form-w-full form-p-2 form-border form-rounded form-bg-white"
                  />
                )}
              </div>
            </div>
            {errors.businessType && <span className="form-text-red-500 form-text-sm">{errors.businessType.message}</span>}
          </div>

          {/* Business Name and Email - Two columns on desktop, single column on mobile */}
          <div className="form-grid form-grid-cols-1 form-sm:grid-cols-2 form-gap-4 form-mb-4">
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business Name*</label>
              <input 
                {...register("businessName", { required: "Business name is required" })} 
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${errors.businessName ? 'form-border-red-500' : 'form-border-gray-300'}`}
              />
              {errors.businessName && <span className="form-text-red-500 form-text-sm">{errors.businessName.message}</span>}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business Email</label>
              <input 
                type="email" 
                {...register("businessEmail")} 
                className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
              />
            </div>
          </div>

          {/* Business Phone and Business Website - Two columns side by side */}
          <div className="form-grid form-grid-cols-1 form-sm:grid-cols-2 form-gap-4 form-mb-4">
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business Phone</label>
              <input 
                type="tel" 
                {...register("businessPhone", {
                  pattern: {
                    value: /^\d{3}-\d{3}-\d{4}$/,
                    message: "Please enter a valid phone number (XXX-XXX-XXXX)"
                  }
                })}
                onChange={handleBusinessPhoneChange}
                placeholder="XXX-XXX-XXXX"
                className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
              />
              {errors.businessPhone && <span className="form-text-red-500 form-text-sm">{errors.businessPhone.message}</span>}
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business Website</label>
              <input 
                {...register("businessWebsite", {
                  validate: {
                    website: (value) => {
                      if (!value) return true; // Optional field
                      return validateWebsite(value) || "Please enter a valid website URL"
                    }
                  }
                })}
                placeholder="www.example.com"
                className={`form-w-full form-p-2 form-border form-rounded form-bg-white ${
                  errors.businessWebsite ? 'form-border-red-500' : 'form-border-gray-300'
                }`}
              />
              {errors.businessWebsite && <span className="form-text-red-500 form-text-sm">{errors.businessWebsite.message}</span>}
            </div>
          </div>

          {/* Business Address Line 1 - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Business Address Line 1</label>
            <input 
              {...register("businessAddress1")}
              className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
            />
          </div>

          {/* Business Address Line 2 - Full width */}
          <div className="form-mb-4">
            <label className="form-block form-mb-1 form-font-bold">Business Address Line 2</label>
            <input 
              {...register("businessAddress2")}
              className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
            />
          </div>

          {/* Business City, State, ZIP row - Three columns */}
          <div className="form-grid form-grid-cols-1 form-sm:grid-cols-3 form-gap-4 form-mb-4">
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business City</label>
              <input 
                {...register("businessCity")}
                className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
              />
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business State</label>
              <select
                {...register("businessState")}
                className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
              >
                {US_STATES.map(state => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-block form-mb-1 form-font-bold">Business ZIP Code</label>
              <input 
                {...register("businessZip", {
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Please enter a valid ZIP code (XXXXX or XXXXX-XXXX)"
                  }
                })}
                onChange={handleBusinessZipChange}
                placeholder="XXXXX"
                className="form-w-full form-p-2 form-border form-rounded form-bg-white form-border-gray-300"
              />
              {errors.businessZip && <span className="form-text-red-500 form-text-sm">{errors.businessZip.message}</span>}
            </div>
          </div>

          {/* "How did you hear about us" - 2 columns */}
          <div className="form-mt-4">
            <label className="form-font-bold">How did you hear about us?*</label>
            <div className="form-grid form-grid-cols-2 form-gap-4 form-mt-2">
              {/* Column 1 */}
              <div>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="radio"
                    {...register("referralSource", { 
                      required: "Please select how you heard about us" 
                    })}
                    value="Google/Search Engine"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Google/Search Engine</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="radio"
                    {...register("referralSource", { 
                      required: "Please select how you heard about us" 
                    })}
                    value="Social Media"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Social Media</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="radio"
                    {...register("referralSource", { 
                      required: "Please select how you heard about us" 
                    })}
                    value="Recommendation"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Recommendation</span>
                </label>
              </div>

              {/* Column 2 */}
              <div>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="radio"
                    {...register("referralSource", { 
                      required: "Please select how you heard about us" 
                    })}
                    value="Tile Mart Website"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Tile Mart Website</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="radio"
                    {...register("referralSource", { 
                      required: "Please select how you heard about us" 
                    })}
                    value="Email"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Email</span>
                </label>
                <label className="form-flex form-items-center form-mb-2">
                  <input
                    type="radio"
                    {...register("referralSource", { 
                      required: "Please select how you heard about us" 
                    })}
                    value="Other"
                  />
                  <span className="form-ml-2 form-text-sm form-sm:text-base">Other</span>
                </label>
              </div>
            </div>
            {errors.referralSource && <span className="form-text-red-500 form-text-sm">{errors.referralSource.message}</span>}
          </div>

          <div className="form-mt-4">
            <label className="form-flex form-items-center">
              <input type="checkbox" {...register("emailUpdates")} />
              <span className="form-ml-2 form-text-sm form-sm:text-base form-font-bold">
                I agree to receive updates and special offers via email. You can manage your email preferences at any time.
              </span>
            </label>
          </div>
        </section>

        {/* Footer with disclaimer and submit button - Center on mobile, right-aligned on desktop */}
        <div className="form-footer">
          <div className="form-text-xs form-sm:text-sm form-text-gray-500">
            *** All applicants are subject to approval by Tile Mart. Submission of application is not a guarantee of approval. We reserve the right to request additional information, deny applicants or terminate a membership at any time.
          </div>
          
          <div className="form-flex form-justify-center form-sm:justify-end form-mt-4 form-sm:mt-0">
            {submitStatus.error && (
              <div className="form-text-red-500 form-mb-4">
                {submitStatus.error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitStatus.loading}
              className="btn"
              style={{
                width: "100%",
                maxWidth: "283px",
                padding: '10px 20px',
                opacity: submitStatus.loading ? '0.5' : '1',
                cursor: submitStatus.loading ? 'not-allowed' : 'pointer'
              }}
            >
              {submitStatus.loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
